import { useState, useEffect, useRef } from 'react';
import { Container, Group, Button, NumberInput, Select } from '@mantine/core';
import { useSelector } from 'react-redux';
import {
  IDispenser,
  IDispenserRequest,
  addDispenserDetails,
  updateDispenserDetails,
  deleteDispenser
} from '../../../libraries/dispensers';
import { AppState } from '../../client-redux/reducers';
import ModalComponent from '../../components/modal';
import { Language } from '../../languages/languageHandler';
import { IProduct, fetchProductsList } from '../../../libraries/products';
import { StatusType } from '../../../libraries/zps';
import { isZarphSuccessResponse, useStatusOperDialog } from '../../context/status-oper-dialog';

interface AddEditDispenserProps {
  dispenser?: IDispenser;
  mode: 'new' | 'edit';
  opened: boolean;
  close: () => void;
  onSaved?: () => void;
  eid?: number;
}

const AddEditDispenser = ({ dispenser, mode, opened, close, onSaved, eid }: AddEditDispenserProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);

  const controller = useRef(new AbortController()).current;
  const [products, setProducts] = useState<IProduct[]>([]);
  const [formData, setFormData] = useState<IDispenserRequest>({
    dispenserid: dispenser?.dispenserid || 0,
    eid: dispenser?.eid || eid || 0,
    productid: dispenser?.productid || '',
    newQuantity: dispenser?.quantity || 0,
    quantity: dispenser?.quantity || 0
  });
  const [loading, setLoading] = useState(false);
  const { showStatusOperDialog, handleApiError } = useStatusOperDialog();

  useEffect(() => {
    const loadProductList = async () => {
      setLoading(true);
      setProducts([]);

      fetchProductsList()
        .then(response => {
          if (isZarphSuccessResponse(response, [9004])) {
            setProducts(response?.data?.list || []);
          } else {
            console.error('Error response:', response);
            handleApiError(response, lang.ALERT_ERROR_FETCHING_DATA);
            setLoading(false);
          }
        })
        .catch(err => {
          // unexpected errors go here
          handleApiError(err, lang.ALERT_ERROR_FETCHING_DATA);
        })
        .finally(() => {
          setLoading(false);
        });
    };

    loadProductList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Initialize form data
  useEffect(() => {
    if (!products?.length) return;

    if (mode === 'new') {
      setFormData(prev => ({
        ...prev,
        eid: eid || 0,
        productid: products[0].productid
      }));
    } else if (mode === 'edit' && dispenser) {
      const matched = products.find(p => p.productid === dispenser.productid);

      setFormData(prev => ({
        ...prev,
        dispenserid: dispenser.dispenserid,
        eid: dispenser.eid,
        productid: matched ? matched.productid : products[0].productid,
        newQuantity: dispenser.quantity,
        quantity: dispenser.quantity
      }));
    }
  }, [products, dispenser, mode, eid]);

  const handleChange = (field: keyof IDispenserRequest, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setLoading(true);

    try {
      const response =
        mode === 'new'
          ? await addDispenserDetails(formData, controller)
          : await updateDispenserDetails(formData, controller);

      if (isZarphSuccessResponse(response, [9004])) {
        showStatusOperDialog({
          type: StatusType.INFO,
          message: mode === 'new' ? lang.DISPENSERS_ADD_SUCCESS : lang.DISPENSERS_EDIT_SUCCESS,
          statusOper: response.data.statusOper
        });

        onSaved?.();
        close();
      } else {
        handleApiError(response, lang.DISPENSERS_ADD_ERROR);
      }
    } catch (err) {
      handleApiError(err, lang.DISPENSERS_ADD_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!dispenser) return;

    setLoading(true);

    try {
      const response = await deleteDispenser(controller, dispenser.entid, dispenser.eid, dispenser.dispenserid);
      if (isZarphSuccessResponse(response, [9004])) {
        showStatusOperDialog({
          type: StatusType.INFO,
          message: lang.DISPENSERS_DELETE_SUCCESS,
          statusOper: response.data.statusOper
        });

        onSaved?.();
        close();
      } else {
        handleApiError(response, lang.DISPENSERS_ADD_ERROR);
      }
    } catch (err) {
      handleApiError(err, lang.DISPENSERS_ADD_ERROR);
    } finally {
      setLoading(false);
    }
  };

  const title = mode === 'new' ? lang.DISPENSERS_ADD : `${lang.DISPENSERS_EDIT} - ${dispenser?.dispenserid}`;

  return (
    <ModalComponent
      show={opened}
      handleClick={close}
      title={title}
      content={
        <Container className="dispenser-details-modal">
          <Group className="dispenser-details">
            <NumberInput
              label={lang.EQUIPMENT_ID}
              value={formData.eid}
              onChange={value => handleChange('eid', value || 0)}
              disabled={mode !== 'new' || loading || !!eid}
            />

            <NumberInput
              label={lang.DISPENSER_ID}
              value={formData.dispenserid}
              onChange={value => handleChange('dispenserid', value || 0)}
              disabled={mode !== 'new' || loading}
              min={0}
            />

            <Select
              label={lang.DISPENSER_PRODUCT}
              value={formData.productid?.toString()}
              onChange={value => handleChange('productid', value)}
              data={
                products?.map(p => ({
                  value: p.productid.toString(),
                  label: `${p.productid} - ${p.name}`
                })) || []
              }
              disabled={loading}
              placeholder="Select product"
            />
          </Group>

          <Group className="dispensers-buttons">
            <Button radius="md" onClick={handleSave} disabled={loading}>
              {lang.GLOBAL_SAVE_BUTTON}
            </Button>

            {mode === 'edit' && (
              <Button radius="md" onClick={handleDelete} disabled={loading}>
                {lang.GLOBAL_DELETE}
              </Button>
            )}
          </Group>
        </Container>
      }
    />
  );
};

export default AddEditDispenser;
